import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowUpRight, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { BOARDING } from '../constants/testIds';
import { useAuth, formatApiError } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: '', email: '', phone: '', nationality: '', residence_country: '', date_of_birth: '',
  id_type: 'Passport', id_number: '',
  investment_capacity: '$1M - $5M', source_of_funds: '', target_region: 'Both', target_property_type: '',
  is_pep: false, accredited_investor: true, notes: '',
};

const STEPS = [
  { title: 'Sovereign Identity', subtitle: 'Step 01' },
  { title: 'Credentials', subtitle: 'Step 02' },
  { title: 'Capital Posture', subtitle: 'Step 03' },
  { title: 'Disclosures', subtitle: 'Step 04' },
];

export default function Boarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...initial, email: user?.email || '', full_name: user?.full_name || '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const stepValid = () => {
    if (step === 0) return form.full_name && form.email && form.phone && form.nationality && form.residence_country && form.date_of_birth;
    if (step === 1) return form.id_type && form.id_number;
    if (step === 2) return form.investment_capacity && form.source_of_funds && form.target_region && form.target_property_type;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/applications`, form, { withCredentials: true });
      setSubmitted(data);
      toast.success('Boarding application transmitted under sovereign seal.');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Transmission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-24 min-h-[80vh] flex items-center" data-testid={BOARDING.successBlock}>
        <div className="max-w-3xl">
          <CheckCircle2 size={40} className="text-[#D4AF37] mb-8" strokeWidth={1} />
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Reference · {submitted.id.slice(0, 8).toUpperCase()}</div>
          <h1 className="text-5xl md:text-6xl font-extralight leading-[1] tracking-tight mb-8">
            Credentials<br />
            <span className="font-display italic text-[#D4AF37]">received.</span>
          </h1>
          <p className="text-base md:text-lg text-[#A3A3A3] leading-relaxed font-light mb-10 max-w-2xl">
            Your boarding dossier is now sealed in Mercy's command queue.
            Approvals are issued within 72 hours — by invitation, under direct admiral review.
          </p>
          <div className="flex flex-wrap gap-6">
            <Link to="/vault" className="btn-sovereign">Survey The Vault <ArrowUpRight size={16} strokeWidth={1.5} /></Link>
            {user && <Link to="/dashboard" className="text-[11px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:text-[#D4AF37] flex items-center gap-2 self-center">Command Deck <ArrowUpRight size={14} strokeWidth={1.5} /></Link>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-12">
        <div className="md:col-span-7">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Boarding Application</div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">
            Identity-anchored<br />
            <span className="font-display italic text-[#D4AF37]">admission.</span>
          </h1>
        </div>
        <div className="md:col-span-5 flex items-end">
          <p className="text-base text-[#A3A3A3] leading-relaxed font-light">
            Reserved for high-net-worth investors. All submissions are sealed,
            reviewed by Mercy's office, and never disclosed beyond the command vessel.
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="grid grid-cols-4 gap-3 mb-16">
        {STEPS.map((s, i) => (
          <div key={i} data-testid={BOARDING.step(i + 1)}
               className={`pb-4 border-b transition-colors ${i <= step ? 'border-[#D4AF37]' : 'border-[#2a2a2a]'}`}>
            <div className={`text-[10px] tracking-[0.3em] uppercase mb-2 ${i <= step ? 'text-[#D4AF37]' : 'text-[#555]'}`}>{s.subtitle}</div>
            <div className={`text-sm tracking-wide font-light ${i <= step ? 'text-[#F5F5F5]' : 'text-[#555]'}`}>{s.title}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-9">
          {step === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 fade-up">
              <Field label="Full Legal Name" testId={BOARDING.fullName} value={form.full_name} onChange={(v) => set('full_name', v)} />
              <Field label="Email" type="email" testId={BOARDING.email} value={form.email} onChange={(v) => set('email', v)} />
              <Field label="Phone" testId={BOARDING.phone} value={form.phone} onChange={(v) => set('phone', v)} />
              <Field label="Nationality" testId={BOARDING.nationality} value={form.nationality} onChange={(v) => set('nationality', v)} />
              <Field label="Country of Residence" testId={BOARDING.residence} value={form.residence_country} onChange={(v) => set('residence_country', v)} />
              <Field label="Date of Birth" type="date" testId={BOARDING.dob} value={form.date_of_birth} onChange={(v) => set('date_of_birth', v)} />
            </div>
          )}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 fade-up">
              <SelectField label="ID Type" testId={BOARDING.idType} value={form.id_type} onChange={(v) => set('id_type', v)}
                options={['Passport', 'National ID', 'Drivers License']} />
              <Field label="ID Number" testId={BOARDING.idNumber} value={form.id_number} onChange={(v) => set('id_number', v)} />
              <div className="md:col-span-2 text-xs tracking-wide text-[#A3A3A3] font-light leading-relaxed mt-4 border-t border-[#D4AF37]/15 pt-6">
                All identity documents are encrypted at the command vessel level. Verification is conducted
                under sovereign chain-of-custody. No third-party processor is involved.
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 fade-up">
              <SelectField label="Investment Capacity" testId={BOARDING.investmentCapacity} value={form.investment_capacity} onChange={(v) => set('investment_capacity', v)}
                options={['$500K - $1M', '$1M - $5M', '$5M - $25M', '$25M+']} />
              <SelectField label="Target Corridor" testId={BOARDING.targetRegion} value={form.target_region} onChange={(v) => set('target_region', v)}
                options={['Nigeria', 'USA / Hawaii', 'Both']} />
              <Field label="Source of Funds" testId={BOARDING.sourceOfFunds} value={form.source_of_funds} onChange={(v) => set('source_of_funds', v)} />
              <Field label="Target Property Type" testId={BOARDING.targetProperty} value={form.target_property_type} onChange={(v) => set('target_property_type', v)}
                placeholder="e.g. Oceanfront villa, Penthouse" />
            </div>
          )}
          {step === 3 && (
            <div className="space-y-8 fade-up">
              <Checkbox testId={BOARDING.accredited} checked={form.accredited_investor} onChange={(v) => set('accredited_investor', v)}
                label="I am an accredited / sophisticated investor under my jurisdiction." />
              <Checkbox testId={BOARDING.isPep} checked={form.is_pep} onChange={(v) => set('is_pep', v)}
                label="I am a Politically Exposed Person (PEP) or related to one." />
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Notes to Mercy</label>
                <textarea data-testid={BOARDING.notes} rows={5} className="input-sovereign resize-none"
                  value={form.notes} onChange={(e) => set('notes', e.target.value)}
                  placeholder="Sovereign objectives, timeline, specific addresses of interest" />
              </div>
            </div>
          )}

          <div className="mt-16 flex items-center justify-between">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
              data-testid={BOARDING.prevBtn}
              className="text-[11px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:text-[#D4AF37] disabled:opacity-30 transition-colors flex items-center gap-2">
              <ArrowLeft size={14} strokeWidth={1.5} /> Previous
            </button>
            {step < STEPS.length - 1 ? (
              <button onClick={() => stepValid() ? setStep((s) => s + 1) : toast.error('Complete the current step.')}
                data-testid={BOARDING.nextBtn} className="btn-sovereign">
                Next Step <ArrowUpRight size={16} strokeWidth={1.5} />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting} data-testid={BOARDING.submitBtn} className="btn-sovereign">
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Transmitting</> : <>Transmit Application <ArrowUpRight size={16} strokeWidth={1.5} /></>}
              </button>
            )}
          </div>
        </div>

        <aside className="md:col-span-3">
          <div className="sticky top-28 sov-border p-6 bg-[#0A0A0A]">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4">Vessel Status</div>
            <div className="space-y-4 text-xs text-[#A3A3A3] font-light leading-relaxed">
              <div className="flex justify-between"><span>Lineage Law</span><span className="text-[#F5F5F5]">75/25</span></div>
              <div className="flex justify-between"><span>Review Time</span><span className="text-[#F5F5F5]">≤ 72 hrs</span></div>
              <div className="flex justify-between"><span>Commander</span><span className="text-[#F5F5F5]">Mercy</span></div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = 'text', testId, placeholder }) => (
  <div>
    <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">{label}</label>
    <input data-testid={testId} type={type} className="input-sovereign" value={value}
      onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required />
  </div>
);

const SelectField = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">{label}</label>
    <select data-testid={testId} className="input-sovereign cursor-pointer" value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const Checkbox = ({ checked, onChange, label, testId }) => (
  <label className="flex items-start gap-4 cursor-pointer group">
    <span className={`flex-shrink-0 mt-1 w-4 h-4 border transition-all ${checked ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-[#555] group-hover:border-[#D4AF37]'}`}>
      {checked && <span className="block w-full h-full" style={{background:'#D4AF37'}}></span>}
    </span>
    <input data-testid={testId} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
    <span className="text-sm font-light text-[#F5F5F5] leading-relaxed">{label}</span>
  </label>
);
